import React, { useEffect, useState, useRef, useContext, useCallback } from "react";
import { destructureLine, getLines, getMove, getMoveAndVariationIndices } from "../util/Chess";
import { Chess } from "chess.js";
import { GameStateContext, useGameState } from "../components/WorkingChessLogic/GameStateContext";
// Remove React-Redux import as it's not used

// Helper function to calculate a hash of moves notation only (not content)
const calculateMovesNotationHash = (moves) => {
  if (!moves || !Array.isArray(moves)) return '';
  
  return moves.map(move => {
    // Only include move notation and variation structure in the hash
    // Exclude content, comments, and other metadata that doesn't affect board position
    let hash = move.notation || '';
    
    // Include variations structure if present
    if (move.variations && Array.isArray(move.variations)) {
      hash += '[' + move.variations.map(variation => {
        if (Array.isArray(variation)) {
          return variation.map(vm => vm.notation || '').join(',');
        }
        return '';
      }).join(';') + ']';
    }
    
    return hash;
  }).join('-');
};

// Also add a function to calculate a unique hash for the current game position
const calculatePositionHash = (moveIdx, moves) => {
  if (!moveIdx || !moves || !Array.isArray(moves)) return 'invalid';
  
  // Handle starting position (both plain and variations)
  if (moveIdx === '-1') return 'start';
  if (moveIdx.startsWith('-1|')) return `start_var_${moveIdx}`;
  
  // For main line moves, include the move index and notation
  if (!moveIdx.includes('|')) {
    const idx = parseInt(moveIdx, 10);
    if (idx < 0 || idx >= moves.length) return 'invalid';
    return `main_${idx}_${moves[idx]?.notation || 'unknown'}`;
  }
  
  // For variation moves, include the full path
  return `var_${moveIdx}`;
};

export default function useMoveIndex({
  moves,
  gameMetadata,
  setGame,
  setMoveContent,
  setVariationContent,
  skipUpdate = false,
}) {
  const [currentMoveIdx, setCurrentMoveIdx] = useState('-1');
  const previousMovesHashRef = useRef('');
  const previousPositionHashRef = useRef('start');
  const skipUpdateRef = useRef(skipUpdate);
  const updateTimeoutRef = useRef(null);
  
  // Add access to the state context to check skipPositionUpdate flag
  const { state, dispatch } = useGameState();
  
  // Update the ref whenever skipUpdate changes
  useEffect(() => {
    skipUpdateRef.current = skipUpdate || (state && state.skipPositionUpdate);
    if (skipUpdate || (state && state.skipPositionUpdate)) {
      
    }
  }, [skipUpdate, state?.skipPositionUpdate]);
  
  // Calculate a hash of the moves notation structure to detect actual move changes
  // rather than just content updates
  const currentMovesHash = calculateMovesNotationHash(moves);
  const hasMovesStructureChanged = currentMovesHash !== previousMovesHashRef.current;
  
  // Calculate a hash for the current position (including starting position variations)
  const currentPositionHash = React.useMemo(() => {
    let hash = calculatePositionHash(currentMoveIdx, moves);
    // Include starting position variations in the hash for proper dependency tracking
    // Use a simpler hash to prevent JSON serialization issues
    if (currentMoveIdx && currentMoveIdx.startsWith('-1|') && state?.startPosition?.variations) {
      const varCount = state.startPosition.variations.length;
      const firstVarLength = state.startPosition.variations[0]?.length || 0;
      hash += `_startVars_${varCount}_${firstVarLength}`;
    }
    return hash;
  }, [currentMoveIdx, moves, state?.startPosition?.variations]);
  
  const hasPositionChanged = currentPositionHash !== previousPositionHashRef.current;
  
  // Update the game board position only when game structure changes, not content
  useEffect(() => {
    // Clear any pending updates
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    
    // Skip updates if explicitly requested OR if skipPositionUpdate is set in context
    if (!gameMetadata || skipUpdateRef.current || (state && state.skipPositionUpdate)) {
      
      
      // If we have a preserved position in the context, we should restore it
      if (state && state.preservedPosition) {
        
        try {
          const recoveryGame = new Chess(state.preservedPosition);
          setGame(recoveryGame);
          
          // Clear the preserved position flag
          setTimeout(() => {
            dispatch({ type: 'RECOVER_POSITION' });
            dispatch({ type: 'CLEAR_SKIP_POSITION_UPDATE' });
          }, 100);
        } catch (err) {
          console.error('useMoveIndex: Error restoring preserved position:', err);
        }
      }
      
      return;
    }
    
    // Debounce rapid updates to prevent infinite loops
    updateTimeoutRef.current = setTimeout(() => {
      performGameUpdate();
    }, 50);
  }, [gameMetadata, currentMoveIdx, setGame, moves, skipUpdate, currentMovesHash, currentPositionHash, state?.skipPositionUpdate, state?.preservedPosition, state?.startPosition]);
  
  // Separate function to perform the actual game update
  const performGameUpdate = useCallback(() => {
    // Update the moves hash reference
    previousMovesHashRef.current = currentMovesHash;
    previousPositionHashRef.current = currentPositionHash;
    
    
    
    const lines = getLines(currentMoveIdx);
    const newGame = new Chess(gameMetadata.fen);
    let currentMoves = moves;
    if (!currentMoves) {
      return;
    }

    if (lines.length === 1) {
      try {
        currentMoves.forEach((move) => {
          try {
            newGame.move(move.notation);
          } catch (err) {
            console.error(`Error applying move ${move.notation}:`, err);
            throw err;
          }
        });
        setGame(newGame);
      } catch (err) {
        console.warn("Stopped applying moves due to error");
      }
      return;
    }

    // Handle starting position variations differently
    if (currentMoveIdx.startsWith('-1|')) {
      // For starting position variations, let UseGameState handle the FEN calculation
      // to prevent duplicate calculations and reduce conflicts
      
      try {
        const newGame = new Chess(gameMetadata.fen);
        
        // Parse the starting position variation path
        const parts = currentMoveIdx.split('|');
        if (parts.length >= 2) {
          const variationPart = parts[1];
          const [varIdx, moveIdx] = variationPart.split(':').map(Number);
          
          // Access starting position variations from GameState context
          if (state && state.startPosition && state.startPosition.variations) {
            const variation = state.startPosition.variations[varIdx];
            
            if (variation && Array.isArray(variation)) {
              // Apply moves from the starting position variation up to the current move
              for (let i = 0; i <= moveIdx && i < variation.length; i++) {
                try {
                  newGame.move(variation[i].notation);
                } catch (err) {
                  console.error(`Error applying starting position variation move ${variation[i].notation}:`, err);
                  break;
                }
              }
            }
          }
        }
        
        setGame(newGame);
      } catch (err) {
        console.error('Error processing starting position variation:', err);
        setGame(new Chess(gameMetadata.fen));
      }
      return;
    }

    try {
      let nextMove;
      for (let line of lines) {
        const [variationNum, movesInThisLine] = destructureLine(line);

        if (variationNum) {
          if (!nextMove || !nextMove.variations) {
            console.warn('useMoveIndex: nextMove or variations is undefined', { nextMove, variationNum });
            return;
          }
          
          currentMoves = nextMove.variations[variationNum];
          
          if (!currentMoves) {
            console.warn('useMoveIndex: variation not found', { variationNum, availableVariations: nextMove.variations.length });
            return;
          }
        }

        for (let i = 0; i <= movesInThisLine; i++) {
          try {
            newGame.move(currentMoves[i].notation);
          } catch (err) {
            console.error(`Error applying move ${currentMoves[i].notation}:`, err);
            throw err;
          }
        }
        nextMove = currentMoves[movesInThisLine];
      }

      setGame(newGame);
    } catch (err) {
      console.warn("Stopped applying variations due to error");
    }
  }, [currentMoveIdx, gameMetadata, moves, currentMovesHash, currentPositionHash, state?.startPosition]);
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  // Update move content only when not skipping updates
  useEffect(() => {
    // Skip if explicitly requested
    if (skipUpdateRef.current) {
      
      return;
    }
    
    // Handle starting position variations differently
    if (currentMoveIdx.startsWith('-1|')) {
      // For starting position variations, get content from startPosition variations
      try {
        const parts = currentMoveIdx.split('|');
        if (parts.length >= 2) {
          const variationPart = parts[1];
          const [varIdx, moveIdx] = variationPart.split(':').map(Number);
          
          if (state && state.startPosition && state.startPosition.variations) {
            const variation = state.startPosition.variations[varIdx];
            if (variation && Array.isArray(variation) && variation[moveIdx]) {
              setMoveContent && setMoveContent(variation[moveIdx].content || '');
            } else {
              setMoveContent && setMoveContent('');
            }
          } else {
            setMoveContent && setMoveContent('');
          }
        } else {
          setMoveContent && setMoveContent('');
        }
      } catch (err) {
        console.error('Error getting starting position variation content:', err);
        setMoveContent && setMoveContent('');
      }
    } else {
      // Regular move content handling
      const currentMove = getMove(moves, currentMoveIdx);

      if (currentMove) {
        setMoveContent && setMoveContent(currentMove.content);
      } else {
        setMoveContent && setMoveContent('');
      }
    }
  }, [gameMetadata, currentMoveIdx, setMoveContent, moves, skipUpdate, state?.startPosition])

  // Update variation content
  useEffect(() => {
    // Skip if explicitly requested
    if (skipUpdateRef.current) {
      
      return;
    }
    
    // ENHANCED: Handle start position variations
    if (currentMoveIdx.startsWith('-1|')) {
      // Don't handle start position variations here - let the main component handle them
      // This prevents conflicts with the main variation content sync logic
      // console.log('[useMoveIndex] Skipping start position variation:', currentMoveIdx);
      return;
    }
    
    const [moveRef, variationIdx] = getMoveAndVariationIndices(currentMoveIdx, moves);
    if (!variationIdx || !moveRef) {
      setVariationContent && setVariationContent('');
      return;
    }

    if (!moveRef?.variationCaptions?.[variationIdx]) {
      setVariationContent && setVariationContent('');
      return;
    }

    setVariationContent && setVariationContent(moveRef.variationCaptions[variationIdx]);
  }, [currentMoveIdx, moves, setVariationContent, skipUpdate]);

  return [currentMoveIdx, setCurrentMoveIdx];
};